/**
 * The "out today" chip on the Grid's day header, next to Today. Built to
 * threads/call-in-chip/mockup.html (PR #2153).
 *
 * The chip is the call-in alert's ONE home: not on the bell, no push. Four faces:
 *
 *   red, glowing        someone still has a job with no tech, or a call-in
 *                       nobody has acknowledged yet
 *   gray with a check   every job is covered and every call-in acknowledged
 *   pink "out tomorrow" nobody out this day, but someone already called in
 *                       for the next day — tap to jump there
 *   (nothing)           nobody out
 *
 * Tapping opens one card per person, worst first: what they said, their jobs
 * with the same tags the call-in page uses, Acknowledge, and Open call-in.
 * Planned time off shows too, in blue, so the list is everyone out that day.
 * On a phone the panel slides up from the bottom.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AlertTriangle, CheckCircle2, ChevronDown, ExternalLink, Info, UserMinus, UserX, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { live, LIVE_FAST_MS } from "@/lib/live-query";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FullMessage } from "@/components/ui/full-message";
import { cn } from "@/lib/utils";

// ── Shapes (mirror src/server/services/outToday.ts) ─────────────────────────

export type OutJobState = "needs_tech" | "covered" | "helper_short" | "done";

export interface OutTodayJob {
    id: string;
    displayNumber: string | null;
    place: string | null;
    city: string | null;
    state: OutJobState;
    leadName: string | null;
}

export interface OutTodayPerson {
    userId: string;
    name: string;
    roleLabel: string | null;
    kind: "call_in" | "planned";
    label: string;
    callInId: string | null;
    reason: string | null;
    reasonHidden: boolean;
    reportedAt: string | null;
    source: string | null;
    acknowledgedAt: string | null;
    acknowledgedByName: string | null;
    jobs: OutTodayJob[];
}

export interface OutTodayResponse {
    date: string;
    people: OutTodayPerson[];
    needsTech: number;
    unacknowledged: number;
    tone: "red" | "clear" | "none";
    nextDay: { date: string; count: number };
    canSeeCallIns: boolean;
    canAcknowledge: boolean;
}

// ── Pure helpers (tested) ───────────────────────────────────────────────────

export type ChipFace = "red" | "clear" | "next" | "none";

/** Which face the chip wears. The next-day face only shows when this day is empty. */
export function chipFace(data: Pick<OutTodayResponse, "tone" | "nextDay"> | undefined): ChipFace {
    if (!data) return "none";
    if (data.tone === "red") return "red";
    if (data.tone === "clear") return "clear";
    return data.nextDay.count > 0 ? "next" : "none";
}

function firstName(name: string | null): string {
    return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

/** The words on one job's tag. Same wording as the call-in page (#2152). */
export function jobTagLabel(job: OutTodayJob): string {
    switch (job.state) {
        case "needs_tech":
            return "Needs a tech";
        case "covered":
            return job.leadName ? `Covered by ${firstName(job.leadName)}` : "Covered";
        case "helper_short":
            return job.leadName ? `Helper short — ${firstName(job.leadName)} still leads` : "Helper short";
        case "done":
        default:
            return "No longer needed";
    }
}

/** "Monday, Nov 2" from a calendar date, never shifted by timezone. */
export function longDayLabel(isoDate: string): string {
    const d = new Date(`${isoDate}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", timeZone: "UTC" });
}

function shortWeekday(isoDate: string): string {
    const d = new Date(`${isoDate}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
}

/** "out today" on today, "out Tue" on any other day. */
export function chipWord(selectedDate: string, isToday: boolean): string {
    return isToday ? "out today" : `out ${shortWeekday(selectedDate)}`;
}

function clock(iso: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("en-US", {
        weekday: "short",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "America/Chicago",
    });
}

function timeOnly(iso: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });
}

/** "Texted Mon 5:48 AM" — how and when they told us. */
export function howTheyToldUs(p: Pick<OutTodayPerson, "kind" | "source" | "reportedAt">): string {
    if (p.kind === "planned") return "Planned ahead";
    const when = clock(p.reportedAt);
    switch (p.source) {
        case "sms":
            return `Texted ${when}`;
        case "app":
            return `Told us in the app ${when}`;
        case "office":
            return `Office logged it ${when}`;
        default:
            return when;
    }
}

type Tone = "red" | "amber" | "blue" | "gray";

/** Card edge: red = a job with no tech; amber = only a helper short; blue = planned. */
export function cardTone(p: OutTodayPerson): Tone {
    if (p.jobs.some((j) => j.state === "needs_tech")) return "red";
    if (p.jobs.some((j) => j.state === "helper_short")) return "amber";
    if (p.kind === "planned") return "blue";
    return p.acknowledgedAt ? "gray" : "red";
}

function initials(name: string): string {
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "")).toUpperCase();
}

// ── Styles ──────────────────────────────────────────────────────────────────

const EDGE: Record<Tone, string> = {
    red: "border-l-rose-600",
    amber: "border-l-amber-500",
    blue: "border-l-sky-500",
    gray: "border-l-stone-400",
};

const AVATAR: Record<Tone, string> = {
    red: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200",
    amber: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-100",
    blue: "bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-100",
    gray: "bg-stone-200 text-stone-700 dark:bg-stone-700 dark:text-stone-100",
};

const JOB_CLASS: Record<OutJobState, string> = {
    needs_tech:
        "border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-500/50 dark:bg-rose-500/10 dark:text-rose-200",
    covered:
        "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/50 dark:bg-emerald-500/10 dark:text-emerald-200",
    helper_short:
        "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-100",
    done: "border-stone-300 bg-stone-50 text-stone-700 dark:border-stone-600 dark:bg-stone-800/40 dark:text-stone-200",
};

const JOB_ICON = {
    needs_tech: AlertTriangle,
    covered: CheckCircle2,
    helper_short: UserMinus,
    done: CheckCircle2,
} as const;

// ── Pieces ──────────────────────────────────────────────────────────────────

function JobRow({ job }: { job: OutTodayJob }) {
    const Icon = JOB_ICON[job.state];
    const where = [job.place, job.city].filter(Boolean).join(" · ");
    return (
        <li>
            <Link
                to={`/jobs/${job.id}`}
                className={cn(
                    "flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border px-2.5 py-2 text-sm hover:underline",
                    JOB_CLASS[job.state],
                )}
            >
                <Icon className="h-4 w-4 shrink-0" aria-hidden />
                <span className="font-bold">{job.displayNumber ? `#${job.displayNumber}` : "Job"}</span>
                <span className="min-w-0">{where}</span>
                <span className="ml-auto font-semibold">{jobTagLabel(job)}</span>
            </Link>
        </li>
    );
}

function PersonCard({
    p,
    canSeeCallIns,
    canAcknowledge,
    acknowledging,
    onAcknowledge,
    onNavigate,
}: {
    p: OutTodayPerson;
    canSeeCallIns: boolean;
    canAcknowledge: boolean;
    acknowledging: boolean;
    onAcknowledge: (id: string) => void;
    onNavigate: () => void;
}) {
    const tone = cardTone(p);
    const planned = p.kind === "planned";
    return (
        <li
            data-testid="out-today-card"
            data-tone={tone}
            className={cn("space-y-3 rounded-lg border border-l-4 bg-card p-3 text-card-foreground", EDGE[tone])}
        >
            <div className="flex items-start gap-2.5">
                <span
                    aria-hidden
                    className={cn(
                        "grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-extrabold",
                        AVATAR[tone],
                    )}
                >
                    {initials(p.name)}
                </span>
                <div className="min-w-0 flex-1">
                    <div className="font-bold leading-tight">{p.name}</div>
                    {p.roleLabel && <div className="text-xs text-foreground/70">{p.roleLabel}</div>}
                </div>
                <div className="shrink-0 text-right">
                    <span
                        className={cn(
                            "inline-block rounded px-1.5 py-0.5 text-xs font-bold",
                            planned
                                ? "bg-sky-600 text-white dark:bg-sky-500"
                                : "bg-violet-600 text-white dark:bg-violet-500",
                        )}
                    >
                        {planned ? `Time off · ${p.label}` : p.label}
                    </span>
                    <div className="mt-0.5 text-xs text-foreground/70">{howTheyToldUs(p)}</div>
                </div>
            </div>

            {!planned && (
                <div>
                    <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-foreground/60">
                        What they said
                    </p>
                    <p className="rounded-md bg-muted/60 px-3 py-2 text-sm leading-relaxed">
                        {p.reasonHidden ? (
                            <span className="text-foreground/70">Only managers can see what they said.</span>
                        ) : p.reason ? (
                            <FullMessage text={p.reason} />
                        ) : (
                            <span className="text-foreground/70">They did not give a reason.</span>
                        )}
                    </p>
                </div>
            )}

            <div>
                <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-foreground/60">Their jobs</p>
                {p.jobs.length === 0 ? (
                    <p className="text-sm text-foreground/70">No jobs on the board for them this day.</p>
                ) : (
                    <ul className="space-y-1.5" onClick={onNavigate}>
                        {p.jobs.map((j) => (
                            <JobRow key={j.id} job={j} />
                        ))}
                    </ul>
                )}
            </div>

            {!planned && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                    {p.acknowledgedAt ? (
                        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                            <CheckCircle2 className="h-4 w-4" aria-hidden />
                            Acknowledged
                            {p.acknowledgedByName ? ` · ${p.acknowledgedByName}, ${timeOnly(p.acknowledgedAt)}` : ""}
                        </span>
                    ) : canAcknowledge && p.callInId ? (
                        <button
                            type="button"
                            disabled={acknowledging}
                            onClick={() => p.callInId && onAcknowledge(p.callInId)}
                            className="w-full rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60 sm:w-auto"
                        >
                            Acknowledge
                        </button>
                    ) : (
                        <span className="text-sm text-foreground/70">Waiting for a manager to acknowledge.</span>
                    )}
                    {canSeeCallIns && p.callInId && (
                        <Link
                            to={`/ci/${p.callInId}`}
                            onClick={onNavigate}
                            className="inline-flex items-center gap-1 text-sm font-semibold text-sky-700 underline underline-offset-2 dark:text-sky-300 max-sm:mx-auto"
                        >
                            Open call-in <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                        </Link>
                    )}
                </div>
            )}
        </li>
    );
}

function Panel({
    data,
    onClose,
    acknowledging,
    onAcknowledge,
}: {
    data: OutTodayResponse;
    onClose: () => void;
    acknowledging: boolean;
    onAcknowledge: (id: string) => void;
}) {
    const n = data.people.length;
    return (
        <div className="flex max-h-[inherit] flex-col" data-testid="out-today-panel">
            <div className="flex items-center gap-2 border-b px-4 py-3">
                <UserX className="h-5 w-5 text-rose-600" aria-hidden />
                <h2 className="text-lg font-bold">Out {longDayLabel(data.date)}</h2>
                <span className="text-sm text-foreground/60">
                    · {n} {n === 1 ? "person" : "people"}
                </span>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="ml-auto rounded p-1 text-foreground/60 hover:bg-muted hover:text-foreground"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
                <p className="flex items-start gap-2 rounded-md bg-muted/60 px-3 py-2 text-sm">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-foreground/60" aria-hidden />
                    <span>
                        Everyone who called in or is off this day.
                        {data.canAcknowledge && (
                            <>
                                {" "}
                                Tap <b>Acknowledge</b> once you have seen it.
                            </>
                        )}
                    </span>
                </p>
                <ul className="space-y-3">
                    {data.people.map((p) => (
                        <PersonCard
                            key={p.userId}
                            p={p}
                            canSeeCallIns={data.canSeeCallIns}
                            canAcknowledge={data.canAcknowledge}
                            acknowledging={acknowledging}
                            onAcknowledge={onAcknowledge}
                            onNavigate={onClose}
                        />
                    ))}
                </ul>
            </div>
            {data.canSeeCallIns && (
                <p className="flex items-center gap-2 border-t px-4 py-2.5 text-sm text-foreground/70">
                    <Info className="h-4 w-4 shrink-0" aria-hidden />
                    <span>
                        Older call-ins live in{" "}
                        <Link
                            to="/people/hr-cockpit"
                            onClick={onClose}
                            className="font-semibold text-sky-700 underline underline-offset-2 dark:text-sky-300"
                        >
                            HR Cockpit
                        </Link>{" "}
                        (People menu).
                    </span>
                </p>
            )}
        </div>
    );
}

/** True on a phone-width screen, where the panel slides up from the bottom. */
function usePhoneWidth(): boolean {
    const query = "(max-width: 639px)";
    const [phone, setPhone] = useState(() =>
        typeof window !== "undefined" && typeof window.matchMedia === "function"
            ? window.matchMedia(query).matches
            : false,
    );
    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
        const mq = window.matchMedia(query);
        const on = () => setPhone(mq.matches);
        mq.addEventListener?.("change", on);
        return () => mq.removeEventListener?.("change", on);
    }, []);
    return phone;
}

// ── The chip ────────────────────────────────────────────────────────────────

export function OutTodayChipView({
    data,
    selectedDate,
    isToday,
    onJumpToDate,
    acknowledging = false,
    onAcknowledge,
    defaultOpen = false,
}: {
    data: OutTodayResponse | undefined;
    selectedDate: string;
    isToday: boolean;
    onJumpToDate: (date: string) => void;
    acknowledging?: boolean;
    onAcknowledge: (id: string) => void;
    defaultOpen?: boolean;
}) {
    const [open, setOpen] = useState(defaultOpen);
    const phone = usePhoneWidth();
    const face = chipFace(data);
    if (!data || face === "none") return null;

    if (face === "next") {
        const n = data.nextDay.count;
        const word = isToday ? "out tomorrow" : `out ${shortWeekday(data.nextDay.date)}`;
        return (
            <button
                type="button"
                data-testid="out-today-chip"
                data-face="next"
                onClick={() => onJumpToDate(data.nextDay.date)}
                title={`${n} ${n === 1 ? "person has" : "people have"} already called in for ${longDayLabel(data.nextDay.date)}. Tap to go to that day.`}
                className="inline-flex h-8 items-center gap-1.5 rounded-full border border-pink-300 bg-pink-100 pl-1 pr-3 text-sm font-bold text-pink-700"
            >
                <span className="grid h-6 min-w-6 place-items-center rounded-full bg-rose-600 px-1 text-xs text-white">
                    {n}
                </span>
                {word}
            </button>
        );
    }

    const n = data.people.length;
    const red = face === "red";
    const chip = (
        <button
            type="button"
            data-testid="out-today-chip"
            data-face={face}
            aria-expanded={open}
            onClick={phone ? () => setOpen(true) : undefined}
            title={
                red
                    ? "Someone still has a job with no tech, or a call-in nobody has acknowledged yet. Tap to see who."
                    : "Everyone out this day is handled: every job covered and every call-in acknowledged. Tap to see who."
            }
            className={cn(
                "inline-flex h-8 items-center gap-1.5 rounded-full border pl-1 pr-2.5 text-sm font-bold transition-colors",
                red
                    ? "border-rose-300 bg-rose-600 text-white shadow-[0_0_0_3px_rgba(225,29,72,0.35),0_0_14px_rgba(225,29,72,0.55)] hover:bg-rose-700"
                    : "border-shell-foreground/25 bg-shell-foreground/15 text-shell-foreground hover:bg-shell-foreground/25",
            )}
        >
            <span
                className={cn(
                    "grid h-6 min-w-6 place-items-center rounded-full px-1 text-xs",
                    red ? "bg-white text-rose-700" : "bg-shell-foreground/30 text-shell-foreground",
                )}
            >
                {n}
            </span>
            {chipWord(selectedDate, isToday)}
            {red ? (
                <ChevronDown className="h-3.5 w-3.5 opacity-80" aria-hidden />
            ) : (
                <CheckCircle2 className="h-4 w-4 text-emerald-300" aria-label="All handled" />
            )}
        </button>
    );

    const panel = (
        <Panel data={data} onClose={() => setOpen(false)} acknowledging={acknowledging} onAcknowledge={onAcknowledge} />
    );

    if (phone) {
        return (
            <>
                {chip}
                <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
                    <DialogPrimitive.Portal>
                        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50" />
                        <DialogPrimitive.Content
                            aria-describedby={undefined}
                            onOpenAutoFocus={(e) => e.preventDefault()}
                            className="fixed inset-x-0 bottom-0 z-50 max-h-[88vh] rounded-t-2xl border-t bg-popover text-popover-foreground shadow-2xl outline-none"
                        >
                            <DialogPrimitive.Title className="sr-only">
                                Out {longDayLabel(data.date)}
                            </DialogPrimitive.Title>
                            <div aria-hidden className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-foreground/20" />
                            <div className="max-h-[calc(88vh-14px)]">{panel}</div>
                        </DialogPrimitive.Content>
                    </DialogPrimitive.Portal>
                </DialogPrimitive.Root>
            </>
        );
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{chip}</PopoverTrigger>
            <PopoverContent
                align="start"
                className="max-h-[min(80vh,760px)] w-[min(560px,calc(100vw-2rem))] p-0"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                {panel}
            </PopoverContent>
        </Popover>
    );
}

/** The chip, wired to GET /api/dispatch/out-today for the Grid's selected day. */
export function OutTodayChip({
    selectedDate,
    isToday,
    onJumpToDate,
}: {
    selectedDate: string;
    isToday: boolean;
    onJumpToDate: (date: string) => void;
}) {
    const qc = useQueryClient();
    const { data } = useQuery({
        queryKey: ["dispatch", "out-today", selectedDate],
        ...live(LIVE_FAST_MS),
        queryFn: () => api.get<OutTodayResponse>(`/api/dispatch/out-today?date=${encodeURIComponent(selectedDate)}`),
    });
    const acknowledge = useMutation({
        mutationFn: (id: string) => api.post(`/api/call-ins/${id}/acknowledge`, {}),
        onSuccess: () => {
            toast.success("Acknowledged.");
            qc.invalidateQueries({ queryKey: ["dispatch", "out-today"] });
            qc.invalidateQueries({ queryKey: ["hr-cockpit"] });
            qc.invalidateQueries({ queryKey: ["call-in"] });
        },
        onError: () => toast.error("That did not save. Check your connection and try again."),
    });
    return (
        <OutTodayChipView
            data={data}
            selectedDate={selectedDate}
            isToday={isToday}
            onJumpToDate={onJumpToDate}
            acknowledging={acknowledge.isPending}
            onAcknowledge={(id) => acknowledge.mutate(id)}
        />
    );
}
